/**
 * Utility to sort and merge recommendations from multiple objectIds.
 * Based on Algolia's official pattern from https://github.com/algolia/instantsearch/blob/master/packages/algoliasearch-helper/src/utils/sortAndMergeRecommendations.js
 */

interface IndexTracker {
  [objectID: string]: {
    indexSum: number
    count: number
  }
}

interface AverageIndex {
  objectID: string
  avgOfIndices: number
}

function getAverageIndices(
  indexTracker: IndexTracker,
  numberOfObjects: number,
): AverageIndex[] {
  const averageIndices: AverageIndex[] = []

  Object.keys(indexTracker).forEach((key) => {
    const trackedItem = indexTracker[key]
    const indexSum =
      trackedItem.count < 2 ? trackedItem.indexSum + 100 : trackedItem.indexSum

    averageIndices.push({
      objectID: key,
      avgOfIndices: indexSum / numberOfObjects,
    })
  })

  return averageIndices.sort((a, b) => {
    return a.avgOfIndices > b.avgOfIndices ? 1 : -1
  })
}

export function sortAndMergeRecommendations<T extends { objectID: string }>(
  results: T[][],
  sourceObjectIds: string[] = [],
): T[] {
  if (results.length === 0) return []
  if (results.length === 1) {
    return results[0].filter((hit) => !sourceObjectIds.includes(hit.objectID))
  }

  const indexTracker: IndexTracker = {}

  results.forEach((hits) => {
    hits.forEach((hit, index) => {
      if (sourceObjectIds.includes(hit.objectID)) return

      if (!indexTracker[hit.objectID]) {
        indexTracker[hit.objectID] = { indexSum: index, count: 1 }
      } else {
        indexTracker[hit.objectID] = {
          indexSum: indexTracker[hit.objectID].indexSum + index,
          count: indexTracker[hit.objectID].count + 1,
        }
      }
    })
  })

  const sortedAverageIndices = getAverageIndices(indexTracker, results.length)

  const flatResults = results.flat()
  const finalOrder = sortedAverageIndices.reduce<T[]>(
    (orderedHits, avgIndexRef) => {
      const result = flatResults.find(
        (hit) => hit.objectID === avgIndexRef.objectID,
      )

      return result ? orderedHits.concat(result) : orderedHits
    },
    [],
  )

  return finalOrder
}
